import React, { Suspense, lazy } from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import { i18nTranslate } from 'src/utils'
import { Modal, Button, Tabs, Tab, Row } from 'react-bootstrap'
import { storeContainer, addressContainer } from 'src/models'
import { nlxxConfigContainer } from 'src/models/NLXX'
import {
  getAddressFieldsJsonBasedOnCountry,
  getAddressOnlyFieldsForValidation,
  checkIfTwoFormValuesAreEqual,
  getValidationErrorMessage,
} from 'src/deps'
import { AddressValidation, toastState } from 'src/views/components'
import {
  removeShippingAddressFormValidationMeta,
  shouldMakeLegacyAddressValidationApiCall,
} from 'src/views/components/CommonFormForCS/shippingAddressFormValidation'
import './styles.scss'

@observer
class SubscriptionAddressModal extends React.Component {
  @observable activeTab = 'savedDetailsTab'
  @observable selectedSavedAddressId = 0
  @observable isToShowAddressSuggestionModal = false
  @observable isEnteredAddressInvalid = false
  @observable recommendationAddresses = []

  currentMarket = storeContainer?.defaultShippingRegion
    ? 'Address' + storeContainer?.defaultShippingRegion
    : 'AddressUS'
  AddressForm = lazy(
    () =>
      import(
        'src/views/addresses/' + this.currentMarket + '/' + this.currentMarket
      )
  )
  componentDidMount() {
    if (
      !nlxxConfigContainer.nlxxConfigStoreId ||
      nlxxConfigContainer.nlxxConfigStoreId !== storeContainer.activeStoreId
    ) {
      nlxxConfigContainer.loadNLXXConfig()
    }
    this.selectedSavedAddressId = this.props?.selectedAddressId || 0
  }

  getFormFieldsBasedOnCountry = async () => {
    let fieldJson = await getAddressFieldsJsonBasedOnCountry(
      storeContainer?.defaultShippingRegion
    )
    let formFields = await getAddressOnlyFieldsForValidation(
      fieldJson,
      'subscription'
    )
    return formFields
  }

  handleAddressModalToggleView = () => {
    this.props.toggleAddressModal()
  }

  handleTabChange = (tabName = '') => {
    this.activeTab = tabName
  }

  handleSubscriptionAddressEdit = (address = {}) => {
    this.props.handleSubscriptionAddressEditModal(address)
  }

  handleAddressSuggestionModalContinue = async (formData = {}) => {
    const { handleAddNewSubscriptionAddress = function () {} } = this.props
    if (formData?.selectedIndex <= 0) {
      let validateOriginalAddressResponse =
        await addressContainer.originalAddressApiCall(formData)
      this.handleAddressSuggestionModalClose()
      if (addressContainer.isSuccessResponse(validateOriginalAddressResponse)) {
        this.handleAddressModalToggleView()
        handleAddNewSubscriptionAddress(formData)
      } else {
        const errorMessageToDisplay = getValidationErrorMessage(
          validateOriginalAddressResponse
        )
        toastState.setToastMessage(errorMessageToDisplay)
      }
    } else {
      this.handleAddressSuggestionModalClose()
      this.handleAddressModalToggleView()
      handleAddNewSubscriptionAddress(formData)
    }
  }

  handleAddressSuggestionModalClose = () => {
    this.isToShowAddressSuggestionModal = false
  }

  handleNewAddressContinue = async (formData = {}, reqBody = {}) => {
    const isToMakeAddressValidationApi =
      shouldMakeLegacyAddressValidationApiCall(
        formData,
        storeContainer?.defaultShippingRegion
      )
    const cleanedFormData = removeShippingAddressFormValidationMeta(formData)
    const validationRequest = removeShippingAddressFormValidationMeta(
      Object.keys(reqBody || {}).length > 0 ? reqBody : formData
    )
    const formDataTemp = {
      ...cleanedFormData,
      zip: cleanedFormData?.zip || cleanedFormData?.postalCode || '',
      shippingInstructions:
        reqBody?.shippingInstructions ||
        cleanedFormData?.shippingInstructions ||
        '',
      setDefaultAddress: cleanedFormData?.setDefaultAddress ?? false,
      country: reqBody?.country || cleanedFormData?.country || '',
    }

    if (isToMakeAddressValidationApi) {
      const res = await addressContainer.validateAddress(validationRequest)
      const recommendationAddress =
        res?.address?.validationDetails?.[0]?.recommendations || []
      if (res?.code === 'ADCOVAL0003') {
        this.recommendationAddresses = [{ ...formDataTemp }, []]
        this.isEnteredAddressInvalid = true
        this.isToShowAddressSuggestionModal = true
      } else if (res?.code == 'ONESOURCE_ADPLGIN_VAL_ERROR') {
        this.isToShowAddressSuggestionModal = false
        toastState.setToastMessage(
          i18nTranslate('address.NotAdded', 'Address not added')
        )
        return
      } else if (addressContainer.isSuccessResponse(res)) {
        const formFields = await this.getFormFieldsBasedOnCountry()
        let firstAddress = recommendationAddress?.[0] || {}
        let isFirstSuggestedAddressSameAsCurrentAddress =
          await checkIfTwoFormValuesAreEqual(
            formFields,
            formDataTemp,
            firstAddress
          )

        if (
          Object.keys(firstAddress)?.length > 0 &&
          !isFirstSuggestedAddressSameAsCurrentAddress
        ) {
          this.recommendationAddresses = [
            { ...formDataTemp },
            ...recommendationAddress,
          ]
          this.isEnteredAddressInvalid = false
          this.isToShowAddressSuggestionModal = true
        } else {
          this.handleAddressSuggestionModalContinue(formDataTemp)
        }
      } else {
        this.isToShowAddressSuggestionModal = false
        toastState.setToastMessage(
          i18nTranslate('address.NotAdded', 'Address not added')
        )
        return
      }
    } else {
      this.handleAddressSuggestionModalContinue(formDataTemp)
    }
  }

  handleSavedAddressContinueButton = () => {
    const { handleSavedAddressContinue = function () {} } = this.props
    const addressData = addressContainer.addressData || []
    const selectedAddress =
      addressData?.find(
        address => address?.addressId === this.selectedSavedAddressId
      ) || {}
    handleSavedAddressContinue(selectedAddress)
    this.handleAddressModalToggleView()
  }

  handleSavedAddressOptionChange = (addressId = 0) => {
    this.selectedSavedAddressId = addressId
  }

  renderSubscriptionSubmitButton = () => {
    return (
      <Modal.Footer className="border-top add-address-tab-footer">
        <Row noGutters className="justify-content-end w-100">
          <Button
            tabIndex={0}
            variant="outline-primary"
            className="add-new-address-close-button"
            onClick={this.handleAddressModalToggleView}
            data-testid="qa-add-new-address-close">
            {i18nTranslate('shipping.closeButton', 'Close')}
          </Button>
          <Button
            tabIndex={0}
            type="submit"
            className="add-new-continue-button"
            variant="primary"
            data-testid="qa-add-new-address-continue">
            {i18nTranslate('shippingMethod.continue', 'Continue')}
          </Button>
        </Row>
      </Modal.Footer>
    )
  }

  renderAddNewAddressForm = AddressForm => {
    const addressData = addressContainer.addressData || []
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <AddressForm
          shippingAddresses={addressData}
          onSubmit={this.handleNewAddressContinue}
          renderSubmitButton={this.renderSubscriptionSubmitButton}
          type="subscription"
        />
      </Suspense>
    )
  }

  renderSavedAddressTabButtons = () => {
    return (
      <>
        <Button
          variant="primary"
          data-testid="qa-saved-address-close"
          className="saved-address-cancel-button"
          onClick={this.handleAddressModalToggleView}>
          {i18nTranslate('shipping.closeButton', 'Close')}
        </Button>
        <Button
          variant="primary"
          data-testid="qa-saved-address-continue"
          className="saved-address-continue-button"
          onClick={this.handleSavedAddressContinueButton}>
          {i18nTranslate('shippingMethod.continue', 'Continue')}
        </Button>
      </>
    )
  }

  renderSavedAddressDetail = (address = {}) => {
    const {
      addressLine1 = '',
      addressLine2 = '',
      street1 = '',
      street2 = '',
      city = '',
      county = '',
      state = '',
      zipCode = '',
      zip = '',
      country = '',
      deliveryInstruction,
      firstName = '',
      lastName = '',
    } = address

    let addressLineOne = addressLine1 || street1 || ''
    let addressLineTwo = addressLine2 || street2 || ''
    const actualZip = zipCode || zip

    const nameDetails = [firstName, lastName].filter(Boolean).join(' ')
    const addressLineDetails = [addressLineOne, addressLineTwo]
      .filter(Boolean)
      .join(', ')
    const addressDetails = [city, county, state, actualZip]
      .filter(Boolean)
      .join(', ')

    return (
      <div
        className="d-flex justify-content-between saved-subscription-address-wrapper"
        data-testid="qa-multiple-subscription-address-option">
        <p className="subscription-address-detail">
          {nameDetails}
          <br />
          {addressLineDetails}
          <br />
          {addressDetails}
          <br />
          {country}
          {deliveryInstruction && (
            <>
              <br />
              {i18nTranslate('shippingInstructions', 'Shipping instructions')}
              {`: ${deliveryInstruction}`}
            </>
          )}
        </p>
        <p className="edit-button-wrapper">
          <button
            tabIndex={0}
            onClick={() => this.handleSubscriptionAddressEdit(address)}
            className="subscription-address-edit-button"
            data-testid="qa-multiple-subscription-address-edit">
            {i18nTranslate('edit', 'Edit')}
          </button>
        </p>
      </div>
    )
  }

  renderSavedAddressRadioOption = (address = {}) => {
    const { addressId = 0 } = address
    return (
      <>
        <input
          type="radio"
          name="addressOption"
          aria-label={addressId}
          key={'option' + addressId}
          id={'option' + addressId}
          onChange={() => {
            this.handleSavedAddressOptionChange(addressId)
          }}
          checked={this.selectedSavedAddressId === addressId}
          data-testId="qa-subscription-saved-address-option"
        />
        <label
          htmlFor={'option' + addressId}
          className={`subscription-option-details-wrapper`}>
          {this.renderSavedAddressDetail(address)}
        </label>
      </>
    )
  }

  renderSavedAddressTabDetails = () => {
    const addressData = addressContainer.addressData || []
    if (addressData?.length) {
      return (
        <div
          className="subscription-saved-address-tab-content"
          data-testid="subscription-saved-addresses-tab-wrapper">
          {addressData?.map((address, index) => {
            return this.renderSavedAddressRadioOption(address)
          })}
        </div>
      )
    }
    return <p>Address</p>
  }

  renderTabView = AddressForm => {
    const { firstTabName = '', secondTabName = '' } = this.props
    return (
      <Tabs
        activeKey={this.activeTab}
        onSelect={tabName => this.handleTabChange(tabName)}
        id="subscription-address-tab"
        unmountOnExit={true}
        className="subscription-address-tabs-wrapper text-capitalize"
        data-testid="qa-subscription-address-tabs-wrapper"
        fill>
        <Tab
          eventKey="savedDetailsTab"
          data-testid="qa-subscription-saved-address"
          className="subscription-saved-address-tab-wrapper"
          title={firstTabName}>
          {this.renderSavedAddressTabDetails()}
          <Modal.Footer>{this.renderSavedAddressTabButtons()}</Modal.Footer>
        </Tab>
        <Tab
          eventKey="addNewDetailsTab"
          data-testid="qa-subscription-add-address"
          className="subscription-add-address-tab-wrapper"
          title={secondTabName}>
          {this.renderAddNewAddressForm(AddressForm)}
        </Tab>
      </Tabs>
    )
  }

  render() {
    const { customModalTitle = '', isToShowAddressModal = false } = this.props

    return (
      <>
        <Modal
          aria-hidden={false}
          centered
          data-testid="qa-multiple-subscription-address-modal"
          show={isToShowAddressModal}
          key="address-suggestion-modal"
          className="subscription-address-edit-modal"
          onHide={this.handleAddressModalToggleView}>
          <Modal.Header closeButton>
            <Modal.Title
              as="h4"
              data-testid="qa-subscription-address-modal-title"
              className="mb-0 subscription-address-edit-modal-title">
              {customModalTitle}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="subscription-address-edit-modal-body">
            {this.renderTabView(this.AddressForm)}
          </Modal.Body>
        </Modal>
        {this.isToShowAddressSuggestionModal && (
          <AddressValidation
            open={this.isToShowAddressSuggestionModal}
            addressInvalidFlag={this.isEnteredAddressInvalid}
            recommendations={this.recommendationAddresses}
            handleClose={this.handleAddressSuggestionModalClose}
            handleClickOnPro={this.handleAddressSuggestionModalContinue}
          />
        )}
      </>
    )
  }
}

export { SubscriptionAddressModal }
export default SubscriptionAddressModal
